import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button, Empty, Field, Input, Notice, Panel, Select, Table, type ColumnDef,
} from '../design/index.js'
import {
  useApi,
  type DataExportView,
  type ImportJobView,
  type ValidatedRow,
} from '../app/api.js'
import { ErrorBlock, Loading, messageFor } from '../framework/feedback.js'
import { formatDateTime } from '../framework/format.js'

/**
 * The three screens in the Data group (docs/11): bulk master loads, the GST
 * rate update, and the whole-account export.
 *
 * The import wizard is three steps and shows its working at every one, because
 * the thing that goes wrong with a bulk load is not that it fails — it is that
 * it succeeds and nobody notices it wrote the wrong thing to two thousand rows.
 */

/* ============================== imports =================================== */

const ACTION_WORDS: Readonly<Record<string, string>> = {
  CREATE: 'New record',
  UPDATE: 'Changed',
  UNCHANGED: 'No change',
  REJECT: 'Cannot be read',
}

/** Reads a picked file as base64, without the data: prefix. */
const readAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

export const DataImports = () => {
  const api = useApi()
  const client = useQueryClient()
  const filePicker = useRef<HTMLInputElement | null>(null)

  const resources = useQuery({ queryKey: ['import-resources'], queryFn: () => api.importResources() })
  const history = useQuery({ queryKey: ['import-history', api.companyId], queryFn: () => api.importHistory() })

  const [resource, setResource] = useState('')
  const [job, setJob] = useState<ImportJobView | null>(null)
  const [error, setError] = useState<string | null>(null)

  const validate = useMutation({
    mutationFn: async (file: File) => api.validateImport(resource, await readAsBase64(file), file.name),
    onSuccess: (result) => { setError(null); setJob(result) },
    onError: (err) => setError(messageFor(err)),
  })

  const commit = useMutation({
    mutationFn: async () => {
      if (!job) throw new Error('Nothing to import.')
      return api.commitImport(job.id)
    },
    onSuccess: (result) => {
      setError(null)
      setJob(result)
      void client.invalidateQueries({ queryKey: ['import-history', api.companyId] })
      // A master that changed underneath every list on the screen.
      void client.invalidateQueries({ queryKey: ['master'] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (resources.isLoading) return <Loading />
  if (resources.error) return <ErrorBlock error={resources.error} />

  const rowColumns: ReadonlyArray<ColumnDef<ValidatedRow>> = [
    { key: 'sheetRow', header: 'Row', width: 80, align: 'right' },
    { key: 'label', header: 'Record' },
    { key: 'action', header: 'What happens', width: 150, render: (r) => ACTION_WORDS[r.action] ?? r.action },
    {
      key: 'changes',
      header: 'Changing',
      render: (r) => (r.problem
        ? r.problem
        : r.changes.length === 0
          ? '—'
          : r.changes.map((c) => `${c.field}: ${c.from} → ${c.to}`).join('; ')),
    },
  ]

  const groups = new Map<string, typeof resources.data>()
  for (const r of resources.data ?? []) {
    const bucket = groups.get(r.group)
    if (bucket) (bucket as Array<typeof r>).push(r)
    else groups.set(r.group, [r] as typeof resources.data)
  }

  const chosen = resources.data?.find((r) => r.key === resource)
  const changing = (job?.rows ?? []).filter((r) => r.action === 'CREATE' || r.action === 'UPDATE')
  const rejected = (job?.rows ?? []).filter((r) => r.action === 'REJECT')

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <Panel title="1 · What are you loading?">
        <Field label="Records">
          <Select
            value={resource}
            onChange={(next) => { setResource(next); setJob(null); setError(null) }}
            placeholder="Choose what to load"
            options={[...groups].flatMap(([group, list]) =>
              (list ?? []).map((r) => ({ value: r.key, label: `${group} — ${r.label}` })))}
          />
        </Field>
        {chosen ? <p className="helper">{chosen.blurb}</p> : null}
      </Panel>

      {resource ? (
        <Panel title="2 · Download, edit, and put it back">
          <p className="helper">
            The spreadsheet comes down filled in with what is already there. Change what you
            need, leave the rest, and upload it. Nothing is written until you have seen what
            it would do.
          </p>
          <div className="row row--wrap">
            <Button
              kind="ghost"
              onClick={() => { window.location.href = api.importTemplateFor(resource) }}
            >
              Download the spreadsheet
            </Button>
            <input
              ref={filePicker}
              type="file"
              accept=".xlsx"
              hidden
              onChange={(event) => {
                const file = event.currentTarget.files?.[0]
                event.currentTarget.value = ''
                if (file) validate.mutate(file)
              }}
            />
            <Button
              kind="primary"
              disabled={validate.isPending}
              onClick={() => filePicker.current?.click()}
            >
              {validate.isPending ? 'Reading…' : 'Choose the edited file'}
            </Button>
          </div>
        </Panel>
      ) : null}

      {job ? (
        <Panel
          title={`3 · ${job.state === 'IMPORTED' ? 'Imported' : 'Check before importing'}`}
          aside={
            job.state === 'IMPORTED' ? null : (
              <Button
                kind="primary"
                disabled={commit.isPending || changing.length === 0}
                onClick={() => commit.mutate()}
              >
                Import {changing.length} row{changing.length === 1 ? '' : 's'}
              </Button>
            )
          }
        >
          {job.state === 'IMPORTED' ? (
            <Notice kind="ok">
              Done. {job.createdCount} created, {job.updatedCount} changed.
            </Notice>
          ) : (
            <p className="helper">
              {changing.length} row{changing.length === 1 ? '' : 's'} would change ·
              {' '}{(job.rows.length - changing.length - rejected.length)} unchanged ·
              {' '}{rejected.length} could not be read.
              {rejected.length > 0 ? ' Rows that cannot be read are skipped, not guessed at.' : ''}
            </p>
          )}
          <Table
            columns={rowColumns}
            rows={[...changing, ...rejected]}
            getKey={(r) => String(r.sheetRow)}
            empty={<Empty title="Nothing would change" body={['Every row in the file already matches what is stored.']} />}
          />
        </Panel>
      ) : null}

      <Panel title="Earlier loads">
        <Table
          columns={[
            { key: 'createdAt', header: 'When', width: 170, render: (r) => formatDateTime(r.createdAt) },
            { key: 'resourceLabel', header: 'What', width: 220 },
            { key: 'fileName', header: 'File', render: (r) => r.fileName ?? '—' },
            { key: 'state', header: 'State', width: 130 },
            { key: 'createdCount', header: 'Created', align: 'right', width: 100 },
            { key: 'updatedCount', header: 'Changed', align: 'right', width: 100 },
            { key: 'rejected', header: 'Skipped', align: 'right', width: 100 },
            { key: 'createdByName', header: 'By', width: 180, render: (r) => r.createdByName ?? '—' },
          ]}
          rows={history.data ?? []}
          getKey={(r) => r.id}
          empty={<Empty title="Nothing loaded yet" body={['Bulk loads appear here once one has been run.']} />}
        />
      </Panel>
    </>
  )
}

/* ============================== exports =================================== */

const EXPORT_WORDS: Readonly<Record<string, string>> = {
  REQUESTED: 'Being built',
  READY: 'Ready to download',
  EXPIRED: 'Expired',
  FAILED: 'Could not be built',
}

export const DataExports = () => {
  const api = useApi()
  const client = useQueryClient()
  const exports_ = useQuery({
    queryKey: ['data-exports', api.companyId],
    queryFn: () => api.dataExports(),
    // One is being built in the background; the row is what says when it is done.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((e: DataExportView) => e.state === 'REQUESTED') ? 5_000 : false,
  })
  const nextAllowed = useQuery({
    queryKey: ['data-export-next', api.companyId],
    queryFn: () => api.dataExportNextAllowed(),
  })
  const [error, setError] = useState<string | null>(null)

  const request = useMutation({
    mutationFn: () => api.requestDataExport(),
    onSuccess: () => {
      setError(null)
      void client.invalidateQueries({ queryKey: ['data-exports', api.companyId] })
      void client.invalidateQueries({ queryKey: ['data-export-next', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (exports_.isLoading) return <Loading />
  if (exports_.error) return <ErrorBlock error={exports_.error} />

  const columns: ReadonlyArray<ColumnDef<DataExportView>> = [
    { key: 'requestedAt', header: 'Asked for', width: 175, render: (r) => formatDateTime(r.requestedAt) },
    { key: 'requestedByName', header: 'By', width: 180, render: (r) => r.requestedByName ?? '—' },
    { key: 'state', header: 'State', width: 165, render: (r) => EXPORT_WORDS[r.state] ?? r.state },
    {
      key: 'byteSize',
      header: 'Size',
      align: 'right',
      width: 110,
      render: (r) => (r.byteSize === null ? '—' : `${Math.max(1, Math.round(r.byteSize / 1024))} KB`),
    },
    {
      key: 'expiresAt',
      header: 'Available until',
      width: 175,
      render: (r) => (r.expiresAt ? formatDateTime(r.expiresAt) : '—'),
    },
    {
      key: 'download',
      header: '',
      width: 150,
      render: (r) => (r.downloadable ? (
        <Button kind="ghost" onClick={() => { window.location.href = api.dataExportUrl(r.id) }}>
          Download
        </Button>
      ) : <span className="helper">{r.note ?? ''}</span>),
    },
  ]

  const blocked = nextAllowed.data?.at ?? null

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}
      <Notice kind="info">
        An export holds every document and every master in this company, as spreadsheets,
        in one archive. The archive is password protected and the password is emailed to
        whoever asked for it — it is not shown here and cannot be read back. The download
        stops working four days after it is built, and one export can be asked for every
        seven days.
      </Notice>

      <Panel
        title="Export everything"
        aside={
          <Button
            kind="primary"
            disabled={request.isPending || blocked !== null}
            onClick={() => request.mutate()}
          >
            Ask for an export
          </Button>
        }
      >
        {blocked ? (
          <p className="helper">
            The next export can be asked for on {formatDateTime(blocked)}.
          </p>
        ) : null}
        <Table
          columns={columns}
          rows={exports_.data ?? []}
          getKey={(r) => r.id}
          empty={<Empty title="No exports yet" body={['Nothing has been exported from this company.']} />}
        />
      </Panel>
    </>
  )
}

/* ============================== branding ================================== */

export const Branding = () => {
  const api = useApi()
  const client = useQueryClient()
  const picker = useRef<HTMLInputElement | null>(null)
  const branding = useQuery({ queryKey: ['branding', api.companyId], queryFn: () => api.branding() })

  const [error, setError] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ website: string; tanNo: string; cinNo: string } | null>(null)

  const current = branding.data
  const values = draft ?? {
    website: current?.website ?? '',
    tanNo: current?.tanNo ?? '',
    cinNo: current?.cinNo ?? '',
  }

  const save = useMutation({
    mutationFn: (input: Parameters<typeof api.saveBranding>[0]) => api.saveBranding(input),
    onSuccess: () => {
      setError(null)
      setDraft(null)
      void client.invalidateQueries({ queryKey: ['branding', api.companyId] })
    },
    onError: (err) => setError(messageFor(err)),
  })

  if (branding.isLoading) return <Loading />
  if (branding.error) return <ErrorBlock error={branding.error} />

  const pickLogo = async (file: File): Promise<void> => {
    // Both checks are made again on the server against the bytes; this one is
    // only so the answer is instant.
    if (file.size > 100 * 1024) {
      setError(`That logo is ${Math.round(file.size / 1024)} kB. It has to be under 100 kB.`)
      return
    }
    const base64 = await readAsBase64(file)
    save.mutate({ logo: `data:image/png;base64,${base64}` })
  }

  return (
    <>
      {error ? <Notice kind="bad">{error}</Notice> : null}

      <Panel title="The sign-in page">
        <p className="helper">
          The logo appears on the sign-in page and at the top of every printed document.
          It has to be a PNG, under 100 kB.
        </p>
        <div className="row row--wrap" style={{ alignItems: 'flex-start', gap: 24 }}>
          <div
            style={{
              border: '1px solid var(--line)',
              padding: 16,
              minWidth: 220,
              minHeight: 96,
              display: 'grid',
              placeItems: 'center',
              background: 'var(--surface-alt)',
            }}
          >
            {current?.logo
              ? <img src={current.logo} alt="The company logo" style={{ maxHeight: 64, maxWidth: 200 }} />
              : <span className="helper">No logo set</span>}
          </div>
          <div className="row row--wrap">
            <input
              ref={picker}
              type="file"
              accept="image/png"
              hidden
              onChange={(event) => {
                const file = event.currentTarget.files?.[0]
                event.currentTarget.value = ''
                if (file) void pickLogo(file)
              }}
            />
            <Button kind="primary" disabled={save.isPending} onClick={() => picker.current?.click()}>
              {current?.logo ? 'Replace the logo' : 'Upload a logo'}
            </Button>
            {current?.logo ? (
              <Button kind="ghost" disabled={save.isPending} onClick={() => save.mutate({ logo: null })}>
                Remove it
              </Button>
            ) : null}
          </div>
        </div>
      </Panel>

      <Panel
        title="What is printed under the company name"
        aside={
          <Button
            kind="primary"
            disabled={save.isPending || draft === null}
            onClick={() => save.mutate(values)}
          >
            Save
          </Button>
        }
      >
        <div className="form-grid">
          <Field label="Website">
            <Input value={values.website} onChange={(website) => setDraft({ ...values, website })} />
          </Field>
          <Field label="TAN" hint="Printed on documents that need it.">
            <Input value={values.tanNo} onChange={(tanNo) => setDraft({ ...values, tanNo })} />
          </Field>
          <Field label="CIN">
            <Input value={values.cinNo} onChange={(cinNo) => setDraft({ ...values, cinNo })} />
          </Field>
        </div>
      </Panel>
    </>
  )
}
